/**
 * SAMA Member Portal - Main App
 * Optimized with code splitting for better performance
 */
import React, { Suspense, lazy } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import BottomNav from './components/BottomNav';
import GlobalOverlays from './components/GlobalOverlays';
import { RequirePermission } from './components/rbac/Permissions';

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Critical pages - loaded immediately
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Schedule from './pages/Schedule';
import BookFirstClass from './pages/BookFirstClass';

// Lazy-loaded pages - loaded on demand
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const ClassDetail = lazy(() => import('./pages/ClassDetail'));
const MyBookings = lazy(() => import('./pages/MyBookings'));
const Account = lazy(() => import('./pages/Account'));
const AccountPreferences = lazy(() => import('./pages/AccountPreferences'));
const Wallet = lazy(() => import('./pages/Wallet'));
const Packages = lazy(() => import('./pages/Packages'));
const FindMyClass = lazy(() => import('./pages/FindMyClass'));
const PlanDetail = lazy(() => import('./pages/PlanDetail'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ClassTypeDetail = lazy(() => import('./pages/ClassTypeDetail'));
const TeamMemberDetail = lazy(() => import('./pages/TeamMemberDetail'));
const WeeklySchedule = lazy(() => import('./pages/WeeklySchedule'));
const Contact = lazy(() => import('./pages/Contact'));
const DirectBooking = lazy(() => import('./pages/DirectBooking'));
const BookingConfirmed = lazy(() => import('./pages/BookingConfirmed'));
const Corporate = lazy(() => import('./pages/Corporate'));
const Franchise = lazy(() => import('./pages/Franchise'));
const FAQ = lazy(() => import('./pages/FAQ'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const PrivateClasses = lazy(() => import('./pages/PrivateClasses'));
const MindfulStrength = lazy(() => import('./pages/MindfulStrength'));
const CompleteProfile = lazy(() => import('./pages/CompleteProfile'));

// Admin pages - lazy loaded (heavy)
// AdminLayout itself is lazy too so guests / marketing-page visitors
// don't download the admin nav chrome + its transitive imports
// (AdminHeader, AdminSidebar, admin-nav.config, plus every icon and
// button imported by the header). Every /admin/* route is nested
// inside the single top-level <Suspense> at line ~141 which already
// shows PageLoader while the layout + page chunk stream in.
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const Admin = lazy(() => import('./pages/Admin'));
const AdminPackages = lazy(() => import('./pages/AdminPackages'));
const AdminMembers = lazy(() => import('./pages/AdminMembers'));
const AdminBookings = lazy(() => import('./pages/AdminBookings'));
const AdminPayments = lazy(() => import('./pages/AdminPayments'));
const AdminWaitlist = lazy(() => import('./pages/AdminWaitlist'));
const AdminAudit = lazy(() => import('./pages/AdminAudit'));
const AdminAnnouncements = lazy(() => import('./pages/AdminAnnouncements'));
const AdminFeedback = lazy(() => import('./pages/AdminFeedback'));
const AdminClassTypes = lazy(() => import('./pages/AdminClassTypes'));
const AdminExperiences = lazy(() => import('./pages/AdminExperiences'));
const ExperiencesList = lazy(() => import('./pages/ExperiencesList'));
const ExperienceDetail = lazy(() => import('./pages/ExperienceDetail'));
const AdminInstructors = lazy(() => import('./pages/AdminInstructors'));
const AdminStudios = lazy(() => import('./pages/AdminStudios'));
const AdminScheduleTemplates = lazy(() => import('./pages/AdminScheduleTemplates'));
const AdminScheduleGenerator = lazy(() => import('./pages/AdminScheduleGenerator'));
const AdminInstructorUsers = lazy(() => import('./pages/AdminInstructorUsers'));
const ManualScheduleGenerator = lazy(() => import('./pages/ManualScheduleGenerator'));
const AdminClassManager = lazy(() => import('./pages/AdminClassManager'));
const AdminCMS = lazy(() => import('./pages/AdminCMS'));
const AdminTheme = lazy(() => import('./pages/AdminTheme'));
const AdminContactCMS = lazy(() => import('./pages/AdminContactCMS'));
const AdminCoupons = lazy(() => import('./pages/AdminCoupons'));
const AdminReferrals = lazy(() => import('./pages/AdminReferrals'));
const AdminBookingSettings = lazy(() => import('./pages/AdminBookingSettings'));
const AdminPaymentLinks = lazy(() => import('./pages/AdminPaymentLinks'));
const AdminEmailBroadcast = lazy(() => import('./pages/AdminEmailBroadcast'));
const AdminOnboardGuest = lazy(() => import('./pages/AdminOnboardGuest'));
const AdminRolesPermissions = lazy(() => import('./pages/AdminRolesPermissions'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));

// Member pages
const ReferAndEarn = lazy(() => import('./pages/ReferAndEarn'));

// CRM pages - lazy loaded
const AdminCRMDashboard = lazy(() => import('./pages/AdminCRMDashboard'));
const AdminCRMLeads = lazy(() => import('./pages/AdminCRMLeads'));
const AdminCRMLeadDetail = lazy(() => import('./pages/AdminCRMLeadDetail'));
const AdminCRMNewLead = lazy(() => import('./pages/AdminCRMNewLead'));

// Instructor pages - lazy loaded
const InstructorLogin = lazy(() => import('./pages/InstructorLogin'));
const InstructorToday = lazy(() => import('./pages/InstructorToday'));
const InstructorSchedule = lazy(() => import('./pages/InstructorSchedule'));
const InstructorRoster = lazy(() => import('./pages/InstructorRoster'));
const InstructorTeamLeaves = lazy(() => import('./pages/InstructorTeamLeaves'));
const InstructorLayout = lazy(() => import('./components/instructor/InstructorLayout'));

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading, member } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  // Auth check MUST run before the profile-completeness check. A user
  // who is logged out (or whose `/auth/me` failed for any reason) must
  // never be funnelled into /complete-profile — that page is for
  // already-authenticated Google OAuth users missing a phone number.
  // We also defensively re-auth if member data is missing, which would
  // otherwise produce a false "incomplete profile" verdict.
  if (!isAuthenticated || !member) {
    return <Navigate to="/login" replace />;
  }

  // Require phone number (Google OAuth users may not have one) — skip for admins
  if (!member?.phone && !member?.is_admin) {
    return <Navigate to="/complete-profile" replace />;
  }
  
  return children;
}

// Redirect helpers for old LP routes
function PrivateClassesRedirect() {
  const { citySlug } = useParams();
  return <Navigate to={`/lp/${citySlug}/private-classes`} replace />;
}
function StrengthRedirect() {
  const { citySlug } = useParams();
  return <Navigate to={`/lp/${citySlug}/strength`} replace />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/complete-profile" element={<CompleteProfile />} />
        <Route path="/schedule" element={<Schedule />} />
        <Route path="/weekly-schedule" element={<WeeklySchedule />} />
        <Route path="/packages" element={<Packages />} />
        <Route path="/plans/:packageSlug" element={<PlanDetail />} />
        <Route path="/class/:slug" element={<ClassTypeDetail />} />
        <Route path="/team/:slug" element={<TeamMemberDetail />} />
        <Route path="/find-my-class" element={<FindMyClass />} />
        <Route path="/contact" element={<Contact />} />
        {/* FAQ page */}
        <Route path="/faq" element={<FAQ />} />
        {/* Privacy Policy / Terms & Conditions / Refunds */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        {/* Corporate landing page */}
        <Route path="/corporate" element={<Corporate />} />
        {/* Franchise landing page */}
        <Route path="/franchise" element={<Franchise />} />
        {/* Private Classes landing page */}
        {/* Private Classes LP - new consistent routes */}
        <Route path="/lp/private-classes" element={<PrivateClasses />} />
        <Route path="/lp/:citySlug/private-classes" element={<PrivateClasses />} />
        {/* Redirects from old routes */}
        <Route path="/private-classes" element={<Navigate to="/lp/private-classes" replace />} />
        <Route path="/private-classes/:citySlug" element={<PrivateClassesRedirect />} />
        {/* Mindful Strength Training LP - new consistent routes */}
        <Route path="/lp/strength" element={<MindfulStrength />} />
        <Route path="/lp/:citySlug/strength" element={<MindfulStrength />} />
        {/* Redirects from old routes */}
        <Route path="/strength" element={<Navigate to="/lp/strength" replace />} />
        <Route path="/strength/:citySlug" element={<StrengthRedirect />} />
        {/* Dynamic landing page with studio and category filters */}
        <Route path="/lp/:studioSlug/:categorySlug/book-first-class" element={<BookFirstClass />} />
        {/* Redirect old URL to new structure */}
        <Route path="/book-first-class" element={<Navigate to="/lp/all/all/book-first-class" replace />} />
        <Route path="/offer" element={<Navigate to="/lp/all/all/book-first-class" replace />} />
        <Route path="/book" element={<DirectBooking />} />
        <Route path="/booking-confirmed" element={<BookingConfirmed />} />
        <Route
          path="/class/:serviceId"
          element={
            <ProtectedRoute>
              <ClassDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-bookings"
          element={
            <ProtectedRoute>
              <MyBookings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <Account />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account-preferences"
          element={
            <ProtectedRoute>
              <AccountPreferences />
            </ProtectedRoute>
          }
        />
        <Route
          path="/refer"
          element={
            <ProtectedRoute>
              <ReferAndEarn />
            </ProtectedRoute>
          }
        />
        {/* Public: Experiences (workshops/events) */}
        <Route path="/experiences" element={<ExperiencesList />} />
        <Route path="/experiences/:id" element={<ExperienceDetail />} />
        <Route
          path="/admin"
          element={<Navigate to="/admin/dashboard" replace />}
        />
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute>
              <AdminLayout><AdminDashboard /></AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/packages"
          element={
            <ProtectedRoute>
              <RequirePermission permission="credit_packages.view">
                <AdminLayout><AdminPackages /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/members"
          element={
            <ProtectedRoute>
              <RequirePermission permission="members.view">
                <AdminLayout><AdminMembers /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bookings"
          element={
            <ProtectedRoute>
              <RequirePermission permission="bookings.view">
                <AdminLayout><AdminBookings /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/payments"
          element={
            <ProtectedRoute>
              <RequirePermission permission="payments.view">
                <AdminLayout><AdminPayments /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/waitlist"
          element={
            <ProtectedRoute>
              <RequirePermission permission="bookings.view">
                <AdminLayout><AdminWaitlist /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <ProtectedRoute>
              <RequirePermission permission="audit_logs.view">
                <AdminLayout><AdminAudit /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/announcements"
          element={
            <ProtectedRoute>
              <AdminLayout><AdminAnnouncements /></AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/experiences"
          element={
            <ProtectedRoute>
              <AdminLayout><AdminExperiences /></AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/feedback"
          element={
            <ProtectedRoute>
              <AdminLayout><AdminFeedback /></AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/class-types"
          element={
            <ProtectedRoute>
              <RequirePermission permission="class_types.view">
                <AdminLayout><AdminClassTypes /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/instructors"
          element={
            <ProtectedRoute>
              <RequirePermission permission="instructors.view">
                <AdminLayout><AdminInstructors /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/studios"
          element={
            <ProtectedRoute>
              <RequirePermission permission="studios.view">
                <AdminLayout><AdminStudios /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/schedule-templates"
          element={
            <ProtectedRoute>
              <RequirePermission permission="schedule_templates.view">
                <AdminLayout><AdminScheduleTemplates /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/schedule-generator"
          element={
            <ProtectedRoute>
              <RequirePermission permission="ai_schedule_generator.view">
                <AdminLayout><AdminScheduleGenerator /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/manual-schedule"
          element={
            <ProtectedRoute>
              <RequirePermission permission="ai_schedule_generator.view">
                <AdminLayout><ManualScheduleGenerator /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/instructor-users"
          element={
            <ProtectedRoute>
              <RequirePermission permission="instructors.view">
                <AdminLayout><AdminInstructorUsers /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route
          path="/wallet"
          element={
            <ProtectedRoute>
              <Wallet />
            </ProtectedRoute>
          }
        />
        {/* CRM Routes */}
        <Route
          path="/admin/crm"
          element={
            <ProtectedRoute>
              <RequirePermission permission="crm.view">
                <AdminLayout><AdminCRMDashboard /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/crm/leads"
          element={
            <ProtectedRoute>
              <RequirePermission permission="crm.view">
                <AdminLayout><AdminCRMLeads /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/crm/leads/new"
          element={
            <ProtectedRoute>
              <RequirePermission permission="crm.edit">
                <AdminLayout><AdminCRMNewLead /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/crm/leads/:leadId"
          element={
            <ProtectedRoute>
              <RequirePermission permission="crm.view">
                <AdminLayout><AdminCRMLeadDetail /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        {/* Class Manager Route */}
        <Route
          path="/admin/class-manager"
          element={
            <ProtectedRoute>
              <RequirePermission permission="class_manager.view">
                <AdminLayout><AdminClassManager /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        {/* CMS Route */}
        <Route
          path="/admin/cms"
          element={
            <ProtectedRoute>
              <RequirePermission permission="content_management.view">
                <AdminLayout><AdminCMS /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        {/* Theme Settings Route */}
        <Route
          path="/admin/theme"
          element={
            <ProtectedRoute>
              <RequirePermission permission="theme_settings.view">
                <AdminLayout><AdminTheme /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        {/* Coupons Route */}
        <Route
          path="/admin/coupons"
          element={
            <ProtectedRoute>
              <RequirePermission permission="coupons.view">
                <AdminLayout><AdminCoupons /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        {/* Referrals Route */}
        <Route
          path="/admin/referrals"
          element={
            <ProtectedRoute>
              <RequirePermission permission="referral_program.view">
                <AdminLayout><AdminReferrals /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        {/* Booking Settings Route */}
        <Route
          path="/admin/booking-settings"
          element={
            <ProtectedRoute>
              <RequirePermission permission="bookings.view">
                <AdminLayout><AdminBookingSettings /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        {/* Payment Links (Phase 1) */}
        <Route
          path="/admin/payment-links"
          element={
            <ProtectedRoute>
              <RequirePermission permission="payment_links.view">
                <AdminLayout><AdminPaymentLinks /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        {/* Onboard Guest — offline payment, first trial */}
        <Route
          path="/admin/onboard-guest"
          element={
            <ProtectedRoute>
              <RequirePermission permission="members.edit">
                <AdminLayout><AdminOnboardGuest /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        {/* Email Broadcast — bulk newsletter to members */}
        <Route
          path="/admin/email-broadcast"
          element={
            <ProtectedRoute>
              <RequirePermission permission="email_broadcast.view">
                <AdminLayout><AdminEmailBroadcast /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        {/* Contact CMS Route */}
        <Route
          path="/admin/contact"
          element={
            <ProtectedRoute>
              <RequirePermission permission="contact_page.view">
                <AdminLayout><AdminContactCMS /></AdminLayout>
              </RequirePermission>
            </ProtectedRoute>
          }
        />
        {/* Roles & Permissions */}
        <Route
          path="/admin/roles"
          element={
            <ProtectedRoute>
              <AdminLayout><AdminRolesPermissions /></AdminLayout>
            </ProtectedRoute>
          }
        />
        {/* Instructor Routes */}
        <Route path="/instructor/login" element={<InstructorLogin />} />
        <Route path="/instructor/today" element={<InstructorLayout><InstructorToday /></InstructorLayout>} />
        <Route path="/instructor/schedule" element={<InstructorLayout><InstructorSchedule /></InstructorLayout>} />
        <Route path="/instructor/roster/:sessionId" element={<InstructorLayout><InstructorRoster /></InstructorLayout>} />
        <Route path="/instructor/team-leave-records" element={<InstructorLayout><InstructorTeamLeaves /></InstructorLayout>} />
        <Route path="/" element={<Home />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <BottomNav />
      <GlobalOverlays />
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
